import { logger } from 'features/logger/logger';
import { markTaskJustCompleted } from 'features/participations/schedule/logic/justCompletedTaskGuard';
import { navigateTo } from 'features/routing/navigationSlice';
import { markIntentionalNavigation } from 'shared/utils/intentionalNavigationGuard';
import { interpolateRedirectUrl } from '../logic/interpolateRedirectUrl';
import { setLoading } from './questionnaireSlice';
import type { ProseCompletionAction } from '../services/proseService';
import type { RedirectUrlPlaceholderName } from '@hub/common';

/**
 * Shared redirect-or-navigate logic used after questionnaire completion.
 *
 * Resolves the redirect URL from the PROSE completion action and the
 * questionnaire's configured redirectUrls, interpolates placeholders,
 * and either redirects (external) or navigates (in-app).
 */
export const redirectOrNavigateAfterCompletion = ({
  completionAction,
  redirectUrls,
  questionnaireId,
  userId,
  participantId,
  participationId,
  activityName,
  dispatch,
  backToRoute,
}: {
  completionAction: ProseCompletionAction | undefined;
  redirectUrls: Record<string, string> | undefined;
  questionnaireId: string;
  userId: string;
  participantId: string | undefined;
  participationId: string;
  activityName: string;
  dispatch: (action: { type: string; payload?: unknown }) => void;
  backToRoute: string;
}): void => {
  const redirectUrl =
    completionAction?.kind === 'redirect'
      ? redirectUrls?.[completionAction.destination]
      : undefined;

  if (completionAction?.kind === 'redirect' && !redirectUrl) {
    logger.warn(
      `Redirect destination "${completionAction.destination}" has no configured URL in redirectUrls`,
      {
        details: {
          destination: completionAction.destination,
          availableDestinations: Object.keys(redirectUrls ?? {}),
        },
      }
    );
  }

  const finalRedirectUrl = redirectUrl
    ? interpolateRedirectUrl(redirectUrl, {
        questionnaire_instance_id: questionnaireId,
        identity_id: userId,
        participant_id: participantId,
      } satisfies Record<RedirectUrlPlaceholderName, string | undefined>)
    : undefined;

  // Guard needed for BOTH paths: external redirect triggers beforeunload (browser dialog),
  // in-app navigateTo triggers useBlocker whose shouldWarn is a stale closure (task status
  // may still be 'touched' in Redux — Firestore snapshot with 'completed' hasn't arrived yet).
  markIntentionalNavigation();

  // The tasks-collection listener can lag the completion write; auto-present
  // must not navigate back into this task while its joined progress still
  // reads in-progress.
  markTaskJustCompleted({ participationId, activityName });

  if (finalRedirectUrl) {
    logger.debug('Redirecting to interpolated URL', {
      details: { finalRedirectUrl },
    });
    dispatch(setLoading('redirecting'));
    window.location.href = finalRedirectUrl;
  } else {
    dispatch(navigateTo(backToRoute));
  }
};
